import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { formatWage, isValidNumber } from '@/services/utils';

interface Employee {
	wage?: number | null;
}

interface WageProps {
	employee: Employee;
	idx: number;
	onChange: (idx: number, field: string, value: number | null) => void;
	showValidation?: boolean;
	disabled?: boolean;
}

const Wage = ({
	employee,
	idx,
	onChange,
	showValidation = false,
	disabled = false
}: WageProps): React.JSX.Element => {
	const { t } = useTranslation();
	const [touched, setTouched] = useState(false);
	const isEmpty = !employee.wage || employee.wage === 0;
	const showError = (touched || showValidation) && isEmpty;

	const processWageValue = (value: string): number | null => {
		const cleanValue = value.replace(/\./g, '');
		if (cleanValue === '') {
			return null;
		}
		if (isValidNumber(cleanValue)) {
			const numValue = Number.parseInt(cleanValue, 10);
			return numValue > 150000 ? employee.wage || null : numValue;
		}
		return employee.wage || null;
	};

	return (
		<div className="flex">
			<span className="inline-flex items-center px-2 rounded-l-md border border-r-0 border-input bg-muted text-xs text-muted-foreground">
				€
			</span>
			<Input
				className={cn(
					'h-8 text-[16px] sm:text-xs rounded-l-none',
					showError && 'border-destructive'
				)}
				value={employee.wage ? formatWage(employee.wage) : ''}
				placeholder={t('common.salary')}
				onChange={(e) => onChange(idx, 'wage', processWageValue(e.target.value))}
				onBlur={(e) => {
					setTouched(true);
					onChange(idx, 'wage', processWageValue(e.target.value));
				}}
				maxLength={7}
				disabled={disabled}
				data-testid={`employee-${idx}-wage-input`}
				required
			/>
		</div>
	);
};

export default Wage;
